import React, { useRef, useEffect } from "react";
import PropTypes from "prop-types";
import "./../../../../assets/scss/common.scss";

export default function AutoResizeTextarea({
  value,
  onChange,
  onBlur,
  className = "",
  maxLines = 5,
  ...rest
}) {
  const ref = useRef(null);

  const adjust = () => {
    const el = ref.current;
    if (!el) return;

    // reset to allow shrinking
    el.style.height = "auto";

    // compute line height from computed styles (fallback to 20)
    const cs = window.getComputedStyle(el);
    const lineHeightVal = cs.lineHeight;
    let lineHeight = 20;
    if (lineHeightVal && lineHeightVal !== "normal") {
      lineHeight = parseFloat(lineHeightVal);
      if (Number.isNaN(lineHeight)) lineHeight = 20;
    } else {
      const fontSize = parseFloat(cs.fontSize) || 14;
      lineHeight = Math.round(fontSize * 1.4);
    }

    const maxHeight = maxLines * lineHeight;

    // scrollHeight includes padding; use it to match content height
    const needed = el.scrollHeight;

    el.style.height = `${Math.min(needed, maxHeight)}px`;
    el.style.overflowY = needed > maxHeight ? "auto" : "hidden";
  };

  useEffect(() => {
    adjust();
    const onResize = () => adjust();
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, [value]);

  return (
    <textarea
      ref={ref}
      className={`autoResizeTextareaStyles ${className}`}
      rows={1}
      value={value}
      onChange={(e) => {
        onChange?.(e.target.value);
        const el = e.target;
        el.style.height = "auto";
        const cs = window.getComputedStyle(el);
        let lineHeight = 20;
        if (cs.lineHeight && cs.lineHeight !== "normal") {
          const parsed = parseFloat(cs.lineHeight);
          if (!Number.isNaN(parsed)) lineHeight = parsed;
        } else {
          const fontSize = parseFloat(cs.fontSize) || 14;
          lineHeight = Math.round(fontSize * 1.4);
        }
        const maxHeight = maxLines * lineHeight;
        const needed = el.scrollHeight;
        el.style.height = `${Math.min(needed, maxHeight)}px`;
        el.style.overflowY = needed > maxHeight ? "auto" : "hidden";
      }}
      onBlur={(e) => onBlur?.(e.target.value)}
      {...rest}
    />
  );
}

AutoResizeTextarea.propTypes = {
  value: PropTypes.string.isRequired,
  onChange: PropTypes.func.isRequired,
  onBlur: PropTypes.func,
  className: PropTypes.string,
  maxLines: PropTypes.number
};
